<!-- ============================================================
     TIMETABLE MANAGEMENT
     Tabs: Class View · Teacher Schedule · Period Setup · Manage
     ============================================================ -->
<div class="tt-page" [class.print-mode]="isPrintMode()">

  <!-- ── Toast ── -->
  @if (saveSuccess()) {
    <div class="tt-toast alert alert-success d-flex align-items-center gap-2 shadow">
      <i class="bi bi-check-circle-fill fs-5"></i><strong>{{ saveSuccess() }}</strong>
    </div>
  }

  <!-- ── Cell Edit Modal ── -->
  @if (showCellModal()) {
    <div class="tt-overlay" (click)="showCellModal.set(false)">
      <div class="tt-modal" (click)="$event.stopPropagation()">
        <div class="tmh">
          <div class="tmh-icon"><i class="bi bi-pencil-fill"></i></div>
          <div>
            <h5 class="tmh-title">{{ editingEntry() ? 'Edit Period' : 'Assign Period' }}</h5>
            <p class="tmh-sub">{{ cellDay }} · {{ getPeriodLabel(cellPeriodId) }}</p>
          </div>
          <button class="tmh-close" (click)="showCellModal.set(false)"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="tmb p-4">
          <div class="row g-3">
            <!-- Class + Section (shown in teacher view) -->
            @if (activeTab() === 'teacher') {
              <div class="col-md-7">
                <label class="form-label fw-semibold">Class <span class="req">*</span></label>
                <select class="form-select" [(ngModel)]="cellForm.class_name">
                  <option value="">-- Select --</option>
                  @for (c of classOptions; track c) { <option [value]="c.code">{{ c.name }}</option> }
                </select>
              </div>
              <div class="col-md-5">
                <label class="form-label fw-semibold">Section</label>
                <select class="form-select" [(ngModel)]="cellForm.class_section">
                  @for (s of sectionOptions; track s) { <option [value]="s.name">{{ s.name }}</option> }
                </select>
              </div>
            }
            <!-- Subject -->
            <div class="col-md-7">
              <label class="form-label fw-semibold">Subject <span class="req">*</span></label>
              <select class="form-select" [(ngModel)]="cellForm.class_subject" (change)="onSubjectChange()"
                *ngIf="subjectList && subjectList?.length">
                <option value="">-- Select Subject --</option>
                @for (subj of subjectList; track subj) {
                  <option [value]="subj.name">{{ subj.name }}</option>
                }
              </select>
            </div>
            <!-- Teacher -->
            <div class="col-md-5">{{cellForm.teacher_id}}
              <label class="form-label fw-semibold">Teacher <span class="req">*</span></label>
              <select class="form-select" [(ngModel)]="cellForm.teacher_id" (change)="onTeacherChange()">
                <option value="">-- Select --</option>
                @for (t of teachers; track t.id) {
                  <option [value]="t.id">{{ t.name }}</option>
                }
              </select>
            </div>
            <!-- Room -->
            <div class="col-12">
              <label class="form-label fw-semibold">Room / Venue</label>
              <select class="form-select" [(ngModel)]="cellForm.class_room">
                <option value="">-- Select Room --</option>
                @for (r of roomOptions; track r) { <option [value]="r.code">{{ r.name }}</option> }
              </select>
            </div>
            <!-- Preview -->
            @if (cellForm.class_subject) {
              <div class="col-12">
                <div class="cell-preview" [style.background]="getSubjectColor(cellForm.class_subject!) + '18'" [style.border-color]="getSubjectColor(cellForm.class_subject!) + '44'">
                  <div class="cp-subject" [style.color]="getSubjectColor(cellForm.class_subject!)">{{ cellForm.class_subject }}</div>
                  @if (cellForm.teacher_id) { <div class="cp-teacher">{{ getTeacherName(cellForm.teacher_id!) }}</div> }
                  @if (cellForm.class_room) { <div class="cp-room"><i class="bi bi-geo-alt me-1"></i>{{ dataService.getRoomName(cellForm.class_room) }}</div> }
                </div>
              </div>
            }
          </div>
        </div>
        <div class="tmf">
          <button class="btn btn-outline-secondary px-4" (click)="showCellModal.set(false)">Cancel</button>
          <button class="btn tt-btn-primary px-5" (click)="saveCell()" [disabled]="!cellForm.class_subject || !cellForm.teacher_id">
            <i class="bi bi-check-circle-fill me-2"></i>{{ editingEntry() ? 'Update' : 'Assign' }}
          </button>
        </div>
      </div>
    </div>
  }

  <!-- ── Period Modal ── -->
  @if (showPeriodModal()) {
    <div class="tt-overlay" (click)="showPeriodModal.set(false)">
      <div class="tt-modal tt-modal-sm" (click)="$event.stopPropagation()">
        <div class="tmh" style="background:linear-gradient(135deg,#059669,#10b981)">
          <div class="tmh-icon"><i class="bi bi-clock-fill"></i></div>
          <div><h5 class="tmh-title">{{ editingPeriod() ? 'Edit Period' : 'Add Period' }}</h5><p class="tmh-sub">Configure timing</p></div>
          <button class="tmh-close" (click)="showPeriodModal.set(false)"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="tmb p-4">
          <div class="row g-3">
            <div class="col-8">
              <label class="form-label fw-semibold">Period Name <span class="req">*</span></label>
              <input type="text" class="form-control" [(ngModel)]="periodForm.pname" maxlength="30" placeholder="e.g. Period 1, Lunch"
                [class.is-invalid]="periodSubmitted && !periodForm.pname">
            </div>
            <div class="col-4">
              <label class="form-label fw-semibold">Order</label>
              <input type="number" class="form-control" [(ngModel)]="periodForm.sort_order" min="1" placeholder="1">
            </div>
            <div class="col-6">
              <label class="form-label fw-semibold">Start Time <span class="req">*</span></label>
              <input type="time" class="form-control" [(ngModel)]="periodForm.start_time"
                [class.is-invalid]="periodSubmitted && !periodForm.start_time">
            </div>
            <div class="col-6">
              <label class="form-label fw-semibold">End Time <span class="req">*</span></label>
              <input type="time" class="form-control" [(ngModel)]="periodForm.end_time"
                [class.is-invalid]="periodSubmitted && !periodForm.end_time">
            </div>
            <div class="col-12">
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" id="isBreak" [(ngModel)]="periodForm.is_break">
                <label class="form-check-label fw-semibold" for="isBreak">This is a Break / Non-teaching Period</label>
              </div>
            </div>
            @if (periodForm.is_break) {
              <div class="col-12">
                <label class="form-label fw-semibold">Break Label</label>
                <input type="text" class="form-control" [(ngModel)]="periodForm.break_label" placeholder="e.g. Lunch Break, Morning Break">
              </div>
            }
          </div>
        </div>
        <div class="tmf">
          <button class="btn btn-outline-secondary px-4" (click)="showPeriodModal.set(false)">Cancel</button>
          <button class="btn tt-btn-primary px-5" (click)="savePeriod()">
            <i class="bi bi-check-circle-fill me-2"></i>{{ editingPeriod() ? 'Update' : 'Add' }}
          </button>
        </div>
      </div>
    </div>
  }

  <!-- ===================== PAGE HEADER ===================== -->
  <div class="tt-ph mb-4 no-print">
    <div>
      <h1 class="tt-title"><i class="bi bi-calendar-week-fill me-2 text-primary" style="font-size:1.1rem;"></i>Timetable Management</h1>
      <p class="tt-subtitle">Class Timetable · Teacher Schedule · Period Setup · Manage</p>
    </div>
    <div class="d-flex gap-2 flex-wrap align-items-start">
      @if (conflicts().length > 0) {
        <button class="btn btn-warning btn-sm d-flex align-items-center gap-1" (click)="showConflicts.set(!showConflicts())">
          <i class="bi bi-exclamation-triangle-fill"></i>
          {{ conflicts().length }} Conflict(s)
        </button>
      }
      <button class="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2" (click)="print()">
        <i class="bi bi-printer-fill"></i>Print
      </button>
    </div>
  </div>

  <!-- Conflict Panel -->
  @if (showConflicts() && conflicts().length > 0) {
    <div class="conflict-panel no-print">
      <div class="cp-header"><i class="bi bi-exclamation-triangle-fill text-warning me-2"></i>Scheduling Conflicts Detected</div>
      @for (c of conflicts(); track c.message) {
        <div class="cp-row"><i class="bi" [class.bi-person-fill]="c.type==='teacher'" [class.bi-door-closed-fill]="c.type==='room'"></i>{{ c.message }}</div>
      }
    </div>
  }

  <!-- ===================== TABS ===================== -->
   <div class="d-flex align-items-center justify-content-between mb-3">
    <div class="tt-tabs no-print">
      @for (tab of [{k:'class',l:'Class Timetable',ic:'bi-grid-fill'},{k:'teacher',l:'Teacher Schedule',ic:'bi-person-workspace'},{k:'periods',l:'Period Setup',ic:'bi-clock-fill'},{k:'manage',l:'Manage',ic:'bi-table'}]; track tab.k) {
        <button class="ttab" [class.active]="activeTab() === tab.k" (click)="activeTab.set($any(tab.k))">
          <i class="bi {{ tab.ic }} me-1"></i>{{ tab.l }}
        </button>
      }
    </div>
    <div class="col-md-4" *ngIf="activeTab() === 'teacher'">
      <!-- <label class="form-label small fw-semibold text-muted">Select Teacher</label> -->
      <select class="form-select" [ngModel]="selectedTeacherId()" (ngModelChange)="selectedTeacherId.set($event)">
        <option>-Select Teacher-</option>
        @for (t of teachers; track t.id) { <option [value]="t.id">{{ t.name }} ({{ dataService.getDeptName(t.department) }})</option> }
      </select>
    </div>
  </div>

  <!-- ===================== CLASS TIMETABLE ===================== -->
  @if (activeTab() === 'class') {
    <!-- Controls -->
    <div class="tt-controls no-print mb-3 d-flex justify-content-between align-items-end">
      <div class="col-md-7 d-flex gap-3">
        <div class="col-md-6">
          <label class="form-label small fw-semibold text-muted">Class</label>
          <select class="form-select" [ngModel]="selectedClass()" (ngModelChange)="selectedClass.set($event)">
            @for (c of classOptions; track c) { <option [value]="c.code">{{ c.name }}</option> }
          </select>
        </div>
        <div class="col-md-3">
          <label class="form-label small fw-semibold text-muted">Section</label>
          <select class="form-select" [ngModel]="selectedSection()" (ngModelChange)="selectedSection.set($event)">
            @for (s of sectionOptions; track s) { <option [value]="s.name">{{ s.name }}</option> }
          </select>
        </div>
        <!-- <div class="col-md-2">
          <label class="form-label small fw-semibold text-muted">Academic Year</label>
          <select class="form-select" [ngModel]="selectedAcYear()" (ngModelChange)="selectedAcYear.set($event)">
            @for (y of academicYears; track y) { <option [value]="y.code">{{ y.name }}</option> }
          </select>
        </div> -->
      </div>
      <!-- Stats row -->
      <div class="col-auto">
        <div class="tt-class-stats">
          <div class="tcs-item"><span class="tcs-val">{{ classStats().filled }}</span><span class="tcs-label">Periods Filled</span></div>
          <div class="tcs-div"></div>
          <div class="tcs-item"><span class="tcs-val text-warning">{{ classStats().empty }}</span><span class="tcs-label">Empty</span></div>
          <div class="tcs-div"></div>
          <div class="tcs-item"><span class="tcs-val text-primary">{{ classStats().subjects }}</span><span class="tcs-label">Subjects</span></div>
          <div class="tcs-div"></div>
          <div class="tcs-item"><span class="tcs-val text-muted">{{ classStats().total }}</span><span class="tcs-label">Total Slots</span></div>
        </div>
      </div>
    </div>

    <!-- Print header -->
    <div class="print-header">
      <h3>EduSmart International School — Weekly Timetable</h3>
      <p>{{ selectedClass() }} · Section {{ selectedSection() }} · {{ selectedAcYear() }}</p>
    </div>

    <!-- Grid -->
    <div class="tt-grid-wrap">
      <div class="tt-grid">
        <!-- Header row -->
        <div class="ttg-cell ttg-header ttg-corner"></div>
        @for (day of DAYS; track day) {
          <div class="ttg-cell ttg-header ttg-day">
            <div class="ttg-day-name">{{ day }}</div>
            <!-- Copy menu -->
            <div class="copy-menu no-print">
              @for (toDay of DAYS.filter(d => d !== day); track toDay) {
                <button class="copy-btn" (click)="copyDayTo(day, toDay)" [title]="'Copy ' + day + ' → ' + toDay">
                  Copy → {{ toDay.slice(0,3) }}
                </button>
              }
            </div>
          </div>
        }

        <!-- Period rows -->
        @for (period of periods(); track period.id) {
          <!-- Period label -->
          <div class="ttg-cell ttg-period-label" [class.ttg-break-label]="period.is_break">
            @if (period.is_break) {
              <div class="break-label-text">{{ period.break_label || period.pname }}</div>
            } @else {
              <div class="period-name">{{ period.pname }}</div>
              <div class="period-time">{{ formatTime(period.start_time) }}</div>
              <div class="period-time period-end">{{ formatTime(period.end_time) }}</div>
            }
          </div>

          <!-- Cells for each day -->
          @for (day of DAYS; track day) {
            @if (period.is_break) {
              <div class="ttg-cell ttg-break-cell">
                <span class="break-chip">{{ period.break_label || 'Break' }}</span>
              </div>
            } @else {
              @let entry = getCell(day, period.id);
              <div class="ttg-cell ttg-slot"
                [class.ttg-filled]="entry !== null"
                [class.ttg-conflict]="entry !== null && isConflictEntry(entry.id)"
                [class.ttg-empty]="entry === null"
                (click)="openCell(day, period.id)">
                @if (entry) {
                  <div class="slot-content" [style.border-left-color]="getSubjectColor(entry.class_subject)">
                    <div class="slot-subject" [style.color]="getSubjectColor(entry.class_subject)">{{ entry.class_subject }}</div>
                    <div class="slot-teacher">{{ entry.teacher_name.split(' ').slice(-1)[0] }}</div>
                    @if (entry.class_room) { <div class="slot-room"><i class="bi bi-geo-alt"></i>{{ dataService.getRoomName(entry.class_room) }}</div> }
                    <!-- Delete button -->
                    <button class="slot-del no-print" (click)="$event.stopPropagation(); deleteCell(day, period.id)" title="Remove"><i class="bi bi-x-lg"></i></button>
                  </div>
                } @else {
                  <div class="slot-empty no-print"><i class="bi bi-plus-lg"></i></div>
                }
              </div>
            }
          }
        }
      </div>
    </div>

    <!-- Subject Legend -->
    @let usedSubjects = classEntries().map(e => e.class_subject).filter((v, i, a) => a.indexOf(v) === i);
    @if (usedSubjects.length > 0) {
      <div class="subject-legend mt-3 no-print">
        @for (subj of usedSubjects; track subj) {
          <div class="sl-item">
            <div class="sl-dot" [style.background]="getSubjectColor(subj)"></div>
            <span>{{ subj }}</span>
          </div>
        }
      </div>
    }
  }

  <!-- ===================== TEACHER SCHEDULE ===================== -->
  @if (activeTab() == 'teacher') {
    <!-- Teacher card -->
    @let teacher = teachers.find(t => t.id == selectedTeacherId());
    @if (teacher) {
      <div class="tt-controls no-print mb-3 py-2">
        <div class="teacher-card-stripX d-flex gap-3 align-items-center justify-content-between">
          <div class="tcs-avatar" [style.background]="teacher.color">{{ teacher.name.charAt(0) }}</div>
          <div>
            <div class="tcs-name">{{ teacher.name }}</div>
            <div class="tcs-meta">{{ dataService.getDesiName(teacher.designation) }} · {{ dataService.getDeptName(teacher.department) }}</div>
            <div class="tcs-subjects">{{ teacher.subjects.join(' · ') }}</div>
          </div>
          <div class="ms-auto d-flex gap-4 text-center">
            <div><div class="fw-bold text-primary">{{ teacherStats().total }}</div><div class="small text-muted">Periods/wk</div></div>
            <div><div class="fw-bold text-success">{{ teacherStats().days }}</div><div class="small text-muted">Days Active</div></div>
            <div><div class="fw-bold text-warning">{{ teacherStats().classes }}</div><div class="small text-muted">Classes</div></div>
          </div>
        </div>
      </div>
    }

    <!-- Print header -->
    @if (teacher) {
      <div class="print-header">
        <h3>Teacher Schedule</h3>
        <p>{{ teacher.name }} · {{ teacher.department }} · {{ selectedAcYear() }}</p>
      </div>
    }

    <!-- Teacher Grid -->
    <div class="tt-grid-wrap">
      <div class="tt-grid">
        <div class="ttg-cell ttg-header ttg-corner"></div>
        @for (day of DAYS; track day) {
          <div class="ttg-cell ttg-header ttg-day"><div class="ttg-day-name">{{ day }}</div></div>
        }
        @for (period of periods(); track period.id) {
          <div class="ttg-cell ttg-period-label" [class.ttg-break-label]="period.is_break">
            @if (period.is_break) {
              <div class="break-label-text">{{ period.break_label || period.pname }}</div>
            } @else {
              <div class="period-name">{{ period.pname }}</div>
              <div class="period-time">{{ formatTime(period.start_time) }}-{{ formatTime(period.end_time) }}</div>
            }
          </div>
          @for (day of DAYS; track day) {
            @if (period.is_break) {
              <div class="ttg-cell ttg-break-cell"><span class="break-chip">{{ period.break_label || 'Break' }}</span></div>
            } @else {
              @let entry = getTeacherCell(day, period.id);
              <div class="ttg-cell ttg-slot"
                [class.ttg-filled]="entry !== null"
                [class.ttg-empty]="entry === null"
                (click)="openTeacherCell(day, period.id)">
                @if (entry) {
                  <div class="slot-content" [style.border-left-color]="getSubjectColor(entry.class_subject)">
                    <div class="slot-subject" [style.color]="getSubjectColor(entry.class_subject)">{{ entry.class_subject }}</div>
                    <div class="slot-teacher">Class {{ dataService.getClassName(entry.class_name) }}-{{ entry.class_section }}</div>
                    @if (entry.class_room) { <div class="slot-room"><i class="bi bi-geo-alt"></i>{{ dataService.getRoomName(entry.class_room) }}</div> }
                    <button class="slot-del no-print" (click)="$event.stopPropagation(); deleteEntry(entry.id)" title="Remove"><i class="bi bi-x-lg"></i></button>
                  </div>
                } @else {
                  <div class="slot-empty no-print"><i class="bi bi-plus-lg"></i></div>
                }
              </div>
            }
          }
        }
      </div>
    </div>
  }

  <!-- ===================== PERIOD SETUP ===================== -->
  @if (activeTab() === 'periods') {
    <div class="d-flex align-items-center justify-content-between mb-3 no-print">
      <h6 class="sub-label mb-0">Period / Timing Configuration</h6>
      <button class="btn tt-btn-primary btn-sm" (click)="openPeriodForm()"><i class="bi bi-plus-circle-fill me-1"></i>Add Period</button>
    </div>
    <div class="periods-grid">
      @for (p of periods(); track p.id) {
        <div class="period-card" [class.pc-break]="p.is_break">
          <div class="pcrd-header">
            <div class="pcrd-icon" [class.pcrd-break-icon]="p.is_break">
              <i class="bi" [class.bi-clock-fill]="!p.is_break" [class.bi-fork-knife]="p.is_break"></i>
            </div>
            <div>
              <div class="pcrd-name">{{ p.pname }}</div>
              @if (p.is_break) { <div class="pcrd-break-tag">Break</div> }
            </div>
            <div class="ms-auto pcrd-time">{{ formatTime(p.start_time) }} – {{ formatTime(p.end_time) }}</div>
          </div>
          @if (p.is_break && p.break_label) {
            <div class="pcrd-break-label">{{ p.break_label }}</div>
          }
          @if (!p.is_break) {
            @let dur = calcPeriodDuration(p);
            <div class="pcrd-duration">{{ dur }} min · Order {{ p.sort_order }}</div>
          }
          <div class="pcrd-actions no-print">
            <button class="pca-btn edit" (click)="openPeriodForm(p)"><i class="bi bi-pencil-fill me-1"></i>Edit</button>
            <button class="pca-btn del" (click)="deletePeriod(p.id)"><i class="bi bi-trash3-fill"></i></button>
          </div>
        </div>
      }
    </div>
  }

  <!-- ===================== MANAGE ===================== -->
  @if (activeTab() === 'manage') {
    <!-- Filters -->
    <div class="tt-card mb-3 no-print">
      <div class="ttc-body">
        <div class="row g-2 align-items-end">
          <div class="col-md-3">
            <label class="form-label small fw-semibold text-muted">Class</label>
            <select class="form-select" [ngModel]="manageClass()" (ngModelChange)="manageClass.set($event)">
              <option value="">All Classes</option>
              @for (c of classOptions; track c) { <option [value]="c.code">{{ c.name }}</option> }
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label small fw-semibold text-muted">Teacher</label>
            <select class="form-select" [ngModel]="manageTeacher()" (ngModelChange)="manageTeacher.set($event)">
              <option value="">All Teachers</option>
              @for (t of teachers; track t.id) { <option [value]="t.id">{{ t.name }}</option> }
            </select>
          </div>
          <div class="col-md-2">
            <label class="form-label small fw-semibold text-muted">Day</label>
            <select class="form-select" [ngModel]="manageDay()" (ngModelChange)="manageDay.set($event)">
              <option value="">All Days</option>
              @for (d of DAYS; track d) { <option [value]="d">{{ d }}</option> }
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label small fw-semibold text-muted">Search</label>
            <input type="text" class="form-control" [ngModel]="manageSearch()" (ngModelChange)="manageSearch.set($event)" placeholder="Subject, teacher, room…">
          </div>
          <div class="col-md-1">
            <button class="btn btn-outline-secondary" (click)="resetManageFilters()"><i class="bi bi-arrow-counterclockwise me-1"></i>Reset</button>
          </div>
        </div>
        <small class="text-muted mt-2 d-block">{{ filteredEntries().length }} Entries · {{ conflicts().length }} Conflict(s)</small>
      </div>
    </div>

    <!-- Manage Table -->
    <div class="tt-card">
      <div class="ttc-body p-0">
        @if (filteredEntries().length === 0) {
          <div class="empty-state"><i class="bi bi-calendar3"></i><p>No timetable entries found</p></div>
        } @else {
          <div class="table-responsive">
            <table class="manage-table">
              <thead><tr>
                <th>Class</th><th>Sec.</th><th>Day</th><th>Period</th>
                <th>Subject</th><th>Teacher</th><th>Room</th>
                <th class="text-center">Status</th><th class="text-center no-print">Actions</th>
              </tr></thead>
              <tbody>
                @for (e of filteredEntries(); track e.id) {
                  <tr [class.conflict-row]="isConflictEntry(e.id)">
                    <td class="small fw-semibold">{{ dataService.getClassName(e.class_name) }} Class</td>
                    <td class="text-center small">{{ e.class_section }}</td>
                    <td class="small">{{ e.class_day.slice(0,3) }}</td>
                    <td class="small text-muted">{{ getPeriodLabel(e.period_id) }}</td>
                    <td>
                      <div class="d-flex align-items-center gap-2">
                        <div class="subj-dot" [style.background]="getSubjectColor(e.class_subject)"></div>
                        <span class="small fw-semibold">{{ e.class_subject }}</span>
                      </div>
                    </td>
                    <td class="small text-secondary">{{ e.teacher_name }}</td>
                    <td class="small text-muted">{{ dataService.getRoomName(e.class_room) || '—' }}</td>
                    <td class="text-center">
                      @if (isConflictEntry(e.id)) {
                        <span class="conflict-badge"><i class="bi bi-exclamation-triangle-fill me-1"></i>Conflict</span>
                      } @else {
                        <span class="ok-badge"><i class="bi bi-check-circle-fill me-1"></i>OK</span>
                      }
                    </td>
                    <td class="text-center no-print">
                      <div class="d-flex gap-2 justify-content-center">
                        <button class="mta" title="Delete" (click)="deleteEntry(e.id)"><i class="bi bi-trash3-fill"></i></button>
                      </div>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </div>
    </div>
  }

</div>
